import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Badge,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useState } from "react";
import TrackingPageLayout from "components/TrackingPageLayout";
import {
  useGuardShifts,
  useGuardSites,
  useGuardAgents,
  createGuardShiftMutation,
} from "hooks/useGuard";
import { useAuthentication } from "hooks/useAuthentication";
import { showToast, toastTypes } from "utils";

function formatDate(s: string | null | undefined) {
  if (!s) return "—";
  return new Date(s).toLocaleString("fr-FR");
}

export default function TrackingGuardShiftsPage() {
  const toast = useToast();
  const { user } = useAuthentication();
  const { shifts, isLoading, error, refetch } = useGuardShifts();
  const { sites } = useGuardSites();
  const { agents } = useGuardAgents();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [submitting, setSubmitting] = useState(false);
  const [agentId, setAgentId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [plannedStart, setPlannedStart] = useState("");
  const [plannedEnd, setPlannedEnd] = useState("");

  const resetForm = useCallback(() => {
    setAgentId("");
    setSiteId("");
    setPlannedStart("");
    setPlannedEnd("");
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = useCallback(async () => {
    if (!agentId || !siteId || !plannedStart || !plannedEnd) {
      showToast({ toast, message: "Veuillez remplir tous les champs requis.", status: toastTypes.error });
      return;
    }
    const start = new Date(plannedStart);
    const end = new Date(plannedEnd);
    if (end <= start) {
      showToast({ toast, message: "La fin doit être après le début.", status: toastTypes.error });
      return;
    }
    setSubmitting(true);
    try {
      await createGuardShiftMutation(user?.auth?.access_token, {
        agent_id: agentId,
        site_id: siteId,
        planned_start: start.toISOString(),
        planned_end: end.toISOString(),
      });
      showToast({ toast, message: "Poste créé avec succès.", status: toastTypes.success });
      refetch();
      handleClose();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || "Erreur lors de la création du poste.";
      showToast({ toast, message: msg, status: toastTypes.error });
    } finally {
      setSubmitting(false);
    }
  }, [agentId, siteId, plannedStart, plannedEnd, user?.auth?.access_token, toast, refetch, handleClose]);

  return (
    <TrackingPageLayout
      title="Postes (shifts)"
      action={
        <Button
          size="sm"
          colorScheme="red_origin"
          variant="outline"
          fontWeight={700}
          borderRadius="8px"
          onClick={onOpen}
        >
          Créer un poste
        </Button>
      }
    >
      {error && (
        <Text color="red.600" mb={3} px={4} pt={4}>
          Erreur lors du chargement des postes.
        </Text>
      )}
      {isLoading ? (
        <Flex justify="center" py={8}><Spinner /></Flex>
      ) : (shifts?.length ?? 0) === 0 ? (
        <Box p={8} textAlign="center"><Text color="gray.600">Aucun poste.</Text></Box>
      ) : (
        <Box overflowX="auto" px={4} pb={4}>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Agent</Th>
                <Th>Site</Th>
                <Th>Poste</Th>
                <Th>Début prévu</Th>
                <Th>Fin prévue</Th>
                <Th>Statut</Th>
              </Tr>
            </Thead>
            <Tbody>
              {shifts.map((s, idx) => (
                <Tr key={s.id ?? idx}>
                  <Td>{(s as any).agent_name ?? "—"}</Td>
                  <Td>{(s as any).site_name ?? "—"}</Td>
                  <Td>{(s as any).post_name ?? "—"}</Td>
                  <Td>{formatDate((s as any).planned_start)}</Td>
                  <Td>{formatDate((s as any).planned_end)}</Td>
                  <Td>
                    <Badge>{(s as any).status ?? "—"}</Badge>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={handleClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Créer un poste (affectation)</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex flexDirection="column" gap={4}>
              <FormControl isRequired>
                <FormLabel>Agent</FormLabel>
                <Select
                  placeholder="Sélectionner un agent"
                  value={agentId}
                  onChange={(e) => setAgentId(e.target.value)}
                >
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Site</FormLabel>
                <Select
                  placeholder="Sélectionner un site"
                  value={siteId}
                  onChange={(e) => setSiteId(e.target.value)}
                >
                  {sites.map((s) => (
                    <option key={String(s.id)} value={String(s.id)}>{s.name}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Début prévu</FormLabel>
                <Input
                  type="datetime-local"
                  value={plannedStart}
                  onChange={(e) => setPlannedStart(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Fin prévue</FormLabel>
                <Input
                  type="datetime-local"
                  value={plannedEnd}
                  onChange={(e) => setPlannedEnd(e.target.value)}
                />
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose}>
              Annuler
            </Button>
            <Button
              colorScheme="red_origin"
              onClick={handleSubmit}
              isLoading={submitting}
            >
              Créer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </TrackingPageLayout>
  );
}
